<template>
  <div>
    <div class="filters-row">
      <div v-if="showBusinessClientFilter" class="filter-group">
        <span class="filter-label">Empresa</span>
        <el-select
          v-model="selectedClient"
          placeholder="Selecione a empresa"
          class="client-filter"
          @change="handleFilterChange"
        >
          <el-option label="Todos" :value="null" />
          <el-option
            v-for="client in effectiveClients"
            :key="client"
            :label="client"
            :value="client"
          />
        </el-select>
      </div>

      <div class="filter-group">
        <span class="filter-label">Data</span>
        <VDatePicker
          :key="`range-${pickerResetKey}`"
          v-model="dateRange"
          mode="date"
          is-range
          :locale="'pt-BR'"
          class="vc-picker vc-picker--filter"
        >
          <template #default="{ togglePopover, inputValue }">
            <div class="vc-input-wrap" @click="togglePopover">
              <button
                type="button"
                class="vc-input-trigger"
                aria-label="Selecionar data ou intervalo"
                @click.stop="togglePopover"
              >
                <el-icon class="vc-input-icon"><Calendar /></el-icon>
              </button>
              <input
                class="vc-input"
                :value="formatDateRangeDisplay(inputValue)"
                readonly
                placeholder="Selecione"
              />
              <button
                v-if="dateRange.start || dateRange.end"
                type="button"
                class="vc-input-clear"
                aria-label="Limpar data"
                @click.stop="clearDateRange"
              >
                ×
              </button>
            </div>
          </template>
        </VDatePicker>
        <span v-if="dateRangeError" class="filter-error">{{ dateRangeError }}</span>
      </div>

      <div class="filter-group">
        <span class="filter-label">Buscar</span>
        <el-input
          v-model="searchInput"
          placeholder="Dispositivo, agendamento ou arquivo"
          maxlength="255"
          class="text-filter"
          clearable
          @input="scheduleSearch"
          @keyup.enter="applySearch"
          @clear="applySearch"
        >
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
      </div>
    </div>

    <div ref="resultsWrapper">
      <el-empty
        v-if="accessRestricted"
        description="Acesso restrito: você não tem permissão para visualizar os dados deste cliente."
      />
      <el-alert
        v-else-if="error"
        :title="error"
        type="error"
        show-icon
        :closable="false"
      />
      <el-empty
        v-else-if="!loading && tableData1.length === 0"
        description="Nenhum log de confirmação encontrado."
      />
      <Table v-else :data="tableData1" :columns="tableColumns1">
        <template #expand="{ row }">
          <ConfirmationScheduleDetails :schedule-id="row.scheduleId" :confirmed-at="row.confirmedAt" />
        </template>
        <template #header-date>
          <span
            class="sortable-header"
            role="button"
            tabindex="0"
            @click="toggleSortOrder"
            @keydown.enter="toggleSortOrder"
          >
            Data
            <el-icon class="sort-icon"><component :is="sortIcon" /></el-icon>
          </span>
        </template>
      </Table>
    </div>

    <div v-if="total > 0" class="pagination-row">
      <span class="pagination-summary">
        {{ count }} de {{ total }} confirmações · página {{ page }} de {{ totalPages }}
      </span>
      <el-select
        :model-value="pageSize"
        placement="top-end"
        class="page-size-select app-select"
        @change="handlePageSizeChange"
      >
        <el-option
          v-for="size in [10, 20, 50, 100]"
          :key="size"
          :label="`${size}/página`"
          :value="size"
        />
      </el-select>
      <el-pagination
        background
        layout="prev, pager, next"
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import Table from "../Component/Table.vue";
import ConfirmationScheduleDetails from "../Schedule/ConfirmationScheduleDetails.vue";
import axios from "axios";
import { config } from "../../config";
import { ElIcon, ElLoading as Loading } from "element-plus";
import { Calendar, ArrowUp, ArrowDown, Search } from "@element-plus/icons-vue";
import { loadBusinessClients } from "../../services/businessClients";
import { clientViewState } from "../../utils/clientView";

function formatFileSize(bytes) {
  const sizeInBytes = Number(bytes);
  const sizeInMB = sizeInBytes / (1024 * 1024);

  return sizeInMB < 0.01
    ? `${sizeInBytes} bytes`
    : `${sizeInMB.toFixed(2)} MB`;
}

function toISODateOnly(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseISODateOnly(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatLogDateTime(isoString) {
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return { date: "", hour: "" };
  return {
    date: d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }),
    hour: d.toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo" }),
  };
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export default {
  name: "ReturnChannel",
  components: {
    Table,
    ConfirmationScheduleDetails,
    ElIcon,
    Calendar,
    Search,
  },
  data() {
    return {
      tableData1: [],
      tableColumns1: [
        { prop: "device", label: "Dispositivo", minWidth: 200 },
        { prop: "date", label: "Data", minWidth: 200 },
        { prop: "hour", label: "Hora", minWidth: 200 },
        { prop: "fileName", label: "Arquivo", minWidth: 200 },
        { prop: "size", label: "Tamanho", minWidth: 200 },
      ],
      loadingInstance: null,
      loading: true,
      error: null,
      accessRestricted: false,
      businessClient: null,
      canChooseBusinessClient: false,
      availableClients: [],
      clientViewState,
      selectedClient: null,
      dateRange: { start: null, end: null },
      dateRangeError: null,
      dateChangeQueued: false,
      suppressDateWatch: true,
      pickerResetKey: 0,
      search: "",
      searchInput: "",
      searchTimer: null,
      sortOrder: "desc",
      pendingQueryBusinessClient: null,
      fetchAbortController: null,
      requestSeq: 0,
      page: 1,
      pageSize: 20,
      count: 0,
      total: 0,
      totalPages: 0,
    };
  },
  computed: {
    showBusinessClientFilter() {
      return this.effectiveClients.length > 1;
    },
    effectiveClients() {
      return this.clientViewState.selectedClient
        ? [this.clientViewState.selectedClient]
        : this.availableClients;
    },
    effectiveBusinessClient() {
      return this.effectiveClients.length === 1 ? this.effectiveClients[0] : null;
    },
    sortIcon() {
      return this.sortOrder === "asc" ? ArrowUp : ArrowDown;
    },
  },
  watch: {
    "clientViewState.selectedClient"() {
      this.applyClientScope();
      this.handleFilterChange();
    },
    dateRange: {
      deep: true,
      handler() {
        if (this.suppressDateWatch) return;
        this.scheduleFilterChange();
      },
    },
  },
  methods: {
    handleFilterChange() {
      this.page = 1;
      this.fetchTableData1();
    },
    scheduleFilterChange() {
      // Coalesce dateRange.start + dateRange.end changing in the same tick into a single fetch.
      if (this.dateChangeQueued) return;
      this.dateChangeQueued = true;
      this.$nextTick(() => {
        this.dateChangeQueued = false;
        this.handleFilterChange();
      });
    },
    applySearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
      const term = this.searchInput.trim();
      if (term === this.search) return;
      this.search = term;
      this.handleFilterChange();
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applySearch(), 350);
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.handleFilterChange();
    },
    clearDateRange() {
      this.dateRange = { start: null, end: null };
      this.pickerResetKey += 1;
    },
    formatDateRangeDisplay(inputValue) {
      if (!inputValue?.start) return "";
      if (!inputValue.end || inputValue.end === inputValue.start) return inputValue.start;
      return `${inputValue.start} – ${inputValue.end}`;
    },
    handlePageChange(newPage) {
      this.page = newPage;
      this.fetchTableData1();
    },
    handlePageSizeChange(newSize) {
      this.pageSize = newSize;
      this.page = 1;
      this.fetchTableData1();
    },
    buildFilterParams() {
      const clientName = this.showBusinessClientFilter
        ? this.selectedClient
        : this.effectiveBusinessClient;

      const params = {};
      if (clientName) params.business_client = clientName;
      if (this.search) params.search = this.search;

      const from = toISODateOnly(this.dateRange?.start);
      const to = toISODateOnly(this.dateRange?.end);
      if (from) params.from = from;
      if (to) params.to = to;

      params.sort_order = this.sortOrder;
      return params;
    },
    syncQueryToUrl(filterParams) {
      const query = { ...filterParams, page: this.page, page_size: this.pageSize };
      const current = this.$route.query;
      const changed =
        Object.keys(query).some((key) => String(query[key]) !== (current[key] ?? "")) ||
        Object.keys(current).some((key) => !(key in query));
      if (!changed) return;
      this.$router.replace({ query }).catch(() => {});
    },
    async fetchTableData1() {
      this.error = null;
      this.accessRestricted = false;

      const from = toISODateOnly(this.dateRange?.start);
      const to = toISODateOnly(this.dateRange?.end);
      if (from && to && from > to) {
        this.dateRangeError = "A data final deve ser maior ou igual à data inicial.";
        return;
      }
      this.dateRangeError = null;

      // Cancel any in-flight request so an older, slower response can't clobber a newer one
      // (each call gets its own loadingInstance/requestId to avoid orphaning the overlay).
      this.fetchAbortController?.abort();
      const controller = new AbortController();
      this.fetchAbortController = controller;
      const requestId = ++this.requestSeq;

      this.loading = true;
      const loadingInstance = Loading.service({
        target: this.$refs.resultsWrapper,
        text: "Carregando dados...",
        spinner: "el-icon-loading",
      });
      this.loadingInstance = loadingInstance;
      try {
        const filterParams = this.buildFilterParams();
        this.syncQueryToUrl(filterParams);

        const { data } = await axios.get(`${config.API}/v1/returnchannel/`, {
          params: { ...filterParams, page: this.page, page_size: this.pageSize },
          signal: controller.signal,
        });

        if (requestId !== this.requestSeq) return;

        const items = data?.items || [];
        this.tableData1 = items.map((item) => {
          const { date, hour } = formatLogDateTime(item.date);

          return {
            device: item.deviceID,
            date,
            hour,
            size: formatFileSize(item.size_bits),
            fileName: item.fileName,
            scheduleId: item.scheduleId,
            confirmedAt: item.date,
          };
        });
        this.count = data?.count ?? items.length;
        this.total = data?.total ?? items.length;
        this.page = data?.page ?? this.page;
        this.pageSize = data?.page_size ?? this.pageSize;
        this.totalPages = data?.total_pages ?? 0;
      } catch (err) {
        if (axios.isCancel(err)) return;
        if (requestId !== this.requestSeq) return;

        console.error("Erro ao buscar dados da tabela:", err);
        if (err?.response?.status === 400) {
          this.dateRangeError = "A data final deve ser maior ou igual à data inicial.";
        } else if (err?.response?.status === 403) {
          this.accessRestricted = true;
        } else {
          this.error = "Erro ao carregar os dados. Tente novamente mais tarde.";
        }
        this.tableData1 = [];
        this.count = 0;
        this.total = 0;
        this.totalPages = 0;
      } finally {
        if (requestId === this.requestSeq) {
          this.loading = false;
          if (this.loadingInstance === loadingInstance) {
            this.loadingInstance = null;
          }
        }
        loadingInstance.close();
      }
    },
    async fetchBusinessClients() {
      try {
        this.availableClients = await loadBusinessClients();
        this.canChooseBusinessClient = this.availableClients.length > 1;
        this.businessClient = this.availableClients.length === 1
          ? this.availableClients[0]
          : null;
        this.selectedClient = this.businessClient;

        if (
          this.pendingQueryBusinessClient &&
          this.effectiveClients.includes(this.pendingQueryBusinessClient)
        ) {
          this.selectedClient = this.pendingQueryBusinessClient;
        }
      } catch (e) {
        console.error("Erro ao buscar clientes disponíveis:", e);
      }
    },
    applyClientScope() {
      this.businessClient = this.effectiveBusinessClient;
      this.selectedClient = this.effectiveBusinessClient;
    },
    initFiltersFromQuery() {
      const q = this.$route.query;
      this.search = typeof q.search === "string" ? q.search : typeof q.deviceID === "string" ? q.deviceID : "";
      this.searchInput = this.search;
      this.sortOrder = q.sort_order === "asc" ? "asc" : "desc";

      const page = parseInt(q.page, 10);
      this.page = Number.isInteger(page) && page > 0 ? page : 1;

      const pageSize = parseInt(q.page_size, 10);
      this.pageSize = PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : 20;

      const start = parseISODateOnly(q.from);
      const end = parseISODateOnly(q.to);
      if (start || end) this.dateRange = { start, end };

      this.pendingQueryBusinessClient = typeof q.business_client === "string"
        ? q.business_client
        : null;
    },
  },
  async mounted() {
    this.initFiltersFromQuery();
    await this.fetchBusinessClients();
    this.$nextTick(() => {
      this.suppressDateWatch = false;
    });
    this.fetchTableData1();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    this.fetchAbortController?.abort();
    this.loadingInstance?.close();
    this.loadingInstance = null;
  },
};
</script>

<style scoped>
@import "@/assets/css/fonts.css";

:deep(.el-table th .cell),
:deep(.el-table td:not(.el-table__expanded-cell) .cell) {
  text-align: center;
}

:deep(.el-table__expanded-cell) {
  padding: 0;
}


.filters-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  row-gap: 14px;
  column-gap: 20px;
  margin-bottom: 25px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: "Globo-Regular", sans-serif;
}

.filter-label {
  color: #2b4054;
  font-size: 14px;
  font-family: "Globo-Regular", sans-serif;
}

.client-filter {
  width: 260px;
}

.text-filter {
  width: 310px;
  flex-shrink: 0;
}

.text-filter :deep(.el-input__wrapper) {
  min-height: 40px;
  box-sizing: border-box;
  box-shadow: 0 0 0 1px #dfe5eb inset;
}

.text-filter :deep(.el-input__wrapper:hover),
.text-filter :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #3a6d8c inset;
}

.text-filter :deep(.el-input__inner) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.filter-error {
  color: #f56c6c;
  font-size: 12px;
  font-family: "Globo-Regular", sans-serif;
}

.sortable-header {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
}

.sortable-header .sort-icon {
  font-size: 13px;
  color: #6f7b87;
}

.client-filter :deep(.el-select__wrapper) {
  height: 40px;
  box-shadow: 0 0 0 1px #dfe5eb inset;
}

.client-filter :deep(.el-select__wrapper.is-hovering:not(.is-focused)),
.client-filter :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #3a6d8c inset;
}

.client-filter :deep(.el-select__selected-item) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.client-filter :deep(.el-select__placeholder.is-transparent) {
  color: #9aa4af;
}

.app-select :deep(.el-select__wrapper) {
  min-height: 40px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

.app-select :deep(.el-select__wrapper.is-hovering:not(.is-focused)),
.app-select :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #3a6d8c inset;
}

.app-select :deep(.el-select__selected-item) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.vc-picker--filter {
  width: 250px;
}

.vc-picker :deep(.vc-popover-content-wrapper) {
  left: 0 !important;
  transform: none !important;
  top: calc(100% + 4px) !important;
}

.vc-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 12px 0 0;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.vc-input-wrap:hover,
.vc-input-wrap:focus-within {
  border-color: #3a6d8c;
}

.vc-input-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.vc-input-icon {
  color: #9aa4af;
  flex-shrink: 0;
}

.vc-input {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 14px;
  font-family: "Globo-Regular", sans-serif;
  color: #2b4054;
  cursor: pointer;
}

.vc-input::placeholder {
  color: #9aa4af;
  font-family: "Globo-Regular", sans-serif;
}

.vc-input-clear {
  border: none;
  background: transparent;
  color: #9aa4af;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px 0 4px;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.pagination-summary {
  color: #6f7b87;
  font-size: 13px;
  font-family: "Globo-Regular", sans-serif;
}

.pagination-row :deep(.el-pagination) {
  font-family: "Globo-Regular", sans-serif;
  --el-color-primary: #3a6d8c;
  --el-pagination-hover-color: #3a6d8c;
}

.pagination-row :deep(.el-pagination.is-background .el-pager li.is-active),
.pagination-row :deep(.el-pagination.is-background .el-pager li.is-active:hover) {
  background-color: #3a6d8c !important;
  color: #fff !important;
}

.page-size-select {
  width: 110px;
}
</style>
